import * as React from 'react';
import NextLink, { LinkProps } from 'next/link';
import { Link as DSLink } from '@zapier/design-system';

import internalRoutes from './routes';

type Props = {
  children: React.ReactNode | string;
  className?: string;
  href: string;
} & LinkProps &
  Omit<React.ComponentProps<typeof DSLink>, 'color'>;

// Does this route match one of our paths in ./routes.ts?
export function routeIsInApp(routes: string[], href: string): boolean {
  // Sanitize href - remove hash and query params
  const path = href.split('?')[0].split('#')[0];
  // See if we have any matching routes
  return routes.some((route: string) => path.startsWith(route));
}

export const Link = React.forwardRef<HTMLAnchorElement, Props>(function Link(
  props,
  ref
) {
  const { target, role } = props;
  try {
    // If we are building a link that is local and can be client-side navigated
    // then we build a Next.js compatible <Link>, otherwise return a regular link.

    const isHrefAbsolute = /^(https?:)?\/\//.test(props.href);

    if (routeIsInApp(internalRoutes, props.href) && !isHrefAbsolute) {
      const { children, className, href, ...restOfLinkProps } = props;
      return (
        <NextLink
          href={href}
          ref={ref}
          className={className}
          {...restOfLinkProps}
          target={target}
          role={role}
        >
          {children}
        </NextLink>
      );
    }

    // eslint-disable-next-line jsx-a11y/anchor-has-content
    return <a {...props} />;
  } catch (e) {
    // Return a vanilla link if we encounter an error parsing the provided
    // `href`. At worst this causes a page refresh to the next page but protets
    // against errors occuring.
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    return <a {...props} />;
  }
});
